import { useState } from 'react'
import { Modal, Button, Form, Spinner } from 'react-bootstrap'
import { useMutation } from '@apollo/client'
import { toast } from 'react-toastify'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import { CREATE_TWILIO_WHATSAPP_TEMPLATE } from '../../gql/mutations/twilio'
import { WhatsAppPreview } from '../templates/components/tiptap/WhatsAppPreview'
import { WhatsAppEditor } from '../templates/components/tiptap/WhatsAppEditor'

const M = defineMessages({
  title: {
    id: 'integrations.twilioCreate.title',
    defaultMessage: 'Create new WhatsApp template (Twilio)',
  },
  internalName: {
    id: 'integrations.twilioCreate.internalName',
    defaultMessage: 'Internal name',
  },
  internalNamePlaceholder: {
    id: 'integrations.twilioCreate.internalNamePlaceholder',
    defaultMessage: 'e.g. payment_reminder_v2',
  },
  internalNameHint: {
    id: 'integrations.twilioCreate.internalNameHint',
    defaultMessage: 'Lowercase letters, numbers, and underscores only.',
  },
  bodyText: {id: 'integrations.template.bodyText', defaultMessage: 'Body text'},
  bodyPlaceholder: {
    id: 'integrations.twilioCreate.bodyPlaceholder',
    defaultMessage: 'Hi {name}, your payment is due on {date}.',
  },
  bodyHint: {
    id: 'integrations.twilioCreate.bodyHint',
    defaultMessage:
      'Use numbered variables (<code>{example}</code>) or named variables supported by your router. Basic markdown (*bold*, _italic_) and emojis are supported visually.',
  },
  footerOptional: {
    id: 'integrations.template.footerOptional',
    defaultMessage: 'Footer (optional)',
  },
  footerPlaceholder: {
    id: 'integrations.twilioCreate.footerPlaceholder',
    defaultMessage: 'Reply STOP to unsubscribe',
  },
  approvalLabel: {
    id: 'integrations.twilioCreate.approvalLabel',
    defaultMessage: 'WhatsApp approval:',
  },
  approvalHint: {
    id: 'integrations.twilioCreate.approvalHint',
    defaultMessage:
      'Upon creation, Twilio will automatically submit the template to WhatsApp for approval. You can sync the status later.',
  },
  previewTitle: {
    id: 'integrations.twilioCreate.previewTitle',
    defaultMessage: 'Preview (approximate)',
  },
  submit: {
    id: 'integrations.twilioCreate.submit',
    defaultMessage: 'Create and submit for approval',
  },
  toastRequired: {
    id: 'integrations.twilioCreate.toast.required',
    defaultMessage: 'Internal name and body text are required.',
  },
  toastCreateFailed: {
    id: 'integrations.twilioCreate.toast.createFailed',
    defaultMessage: 'Failed to create template',
  },
  toastApiError: {
    id: 'integrations.twilioCreate.toast.apiError',
    defaultMessage: 'Critical error communicating with Twilio API',
  },

  // Reusadas
  language: {id: 'comms.templates.variant.waLanguage', defaultMessage: 'Language'},
  cancel: {id: 'common.action.cancel', defaultMessage: 'Cancel'},
})

// i18n-ok-start: los nombres de idioma van en su propio idioma, como en cualquier
// selector de idioma; el `value` ademas viaja a Twilio.
const LANGUAGE_OPTIONS = [
  {value: 'es', label: 'Español (es)'},
  {value: 'es_LA', label: 'Español Latinoamericano (es_LA)'},
  {value: 'en', label: 'English (en)'},
  {value: 'en_US', label: 'English US (en_US)'},
]

// Ejemplos de sintaxis de variable de Twilio: son datos del ejemplo, no prosa.
const WA_VAR_CONTACT_NAME = '{{Contact.first_name}}'
const WA_VAR_NUMBERED = '{{1}}'
// i18n-ok-end

export const CreateTwilioTemplateModal = ({
  show,
  onHide,
  onSuccess
}: {
  show: boolean
  onHide: () => void
  onSuccess: () => void
}) => {
  const intl = useIntl()
  const [form, setForm] = useState({
    friendly_name: '',
    language: 'es',
    body_text: '',
    footer_text: ''
  })

  const [createTemplate, { loading }] = useMutation(CREATE_TWILIO_WHATSAPP_TEMPLATE)

  const handleChange = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const handleCreate = async () => {
    if (!form.friendly_name || !form.body_text) {
      toast.warning(intl.formatMessage(M.toastRequired))
      return
    }

    // Convert string keys like {{1}} format. For Twilio variables are typically {{1}}, {{2}}...
    // In this MVP we just pass text, Twilio infers variables from {{1}}.
    try {
      const { data } = await createTemplate({
        variables: {
          input: {
            friendly_name: form.friendly_name.toLowerCase().replace(/\s+/g, '_'),
            language: form.language,
            body_text: form.body_text,
            footer_text: form.footer_text || null
          }
        }
      })

      if (data?.createTwilioWhatsappTemplate?.success) {
        toast.success(data.createTwilioWhatsappTemplate.message)
        onSuccess()
        onHide()
        setForm({ friendly_name: '', language: 'en_US', body_text: '', footer_text: '' })
      } else {
        toast.error(
          data?.createTwilioWhatsappTemplate?.message || intl.formatMessage(M.toastCreateFailed)
        )
      }
    } catch (e: any) {
      toast.error(e.message || intl.formatMessage(M.toastApiError))
    }
  }

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>{intl.formatMessage(M.title)}</Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        <div className="row">
          <div className="col-md-7">
            <Form>
              <Form.Group className="mb-3">
                <Form.Label className="required">
                  {intl.formatMessage(M.internalName)}
                </Form.Label>
                <Form.Control 
                  type="text" 
                  placeholder={intl.formatMessage(M.internalNamePlaceholder)}
                  value={form.friendly_name}
                  onChange={(e) => handleChange('friendly_name', e.target.value)}
                />
                <Form.Text className="text-muted">
                  {intl.formatMessage(M.internalNameHint)}
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="required">{intl.formatMessage(M.language)}</Form.Label>
                <Form.Select 
                  value={form.language}
                  onChange={(e) => handleChange('language', e.target.value)}
                >
                  {LANGUAGE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="required">{intl.formatMessage(M.bodyText)}</Form.Label>
                <WhatsAppEditor
                  value={form.body_text}
                  onChange={(text) => handleChange('body_text', text)}
                  height={200}
                  placeholder={intl.formatMessage(M.bodyPlaceholder, {
                    name: WA_VAR_CONTACT_NAME,
                    date: WA_VAR_NUMBERED,
                  })}
                  // i18n-ok: nombres de variable que se insertan en el cuerpo del mensaje, no interfaz
                  variables={['Contact.first_name', 'Contact.last_name', 'Contact.email', 'Input_text', 'Input_number', '1', '2', '3']}
                  getVariableText={(v) => `{{${v}}}`}
                />
                <Form.Text className="text-muted">
                    <FormattedMessage
                      {...M.bodyHint}
                      values={{
                        code: (chunks) => <code>{chunks}</code>,
                        example: WA_VAR_NUMBERED,
                      }}
                    />
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>{intl.formatMessage(M.footerOptional)}</Form.Label>
                <Form.Control 
                  type="text" 
                  placeholder={intl.formatMessage(M.footerPlaceholder)}
                  value={form.footer_text}
                  onChange={(e) => handleChange('footer_text', e.target.value)}
                  maxLength={60}
                />
              </Form.Group>

            </Form>
            
            <div className="alert alert-primary mt-4">
               <i className="bi bi-info-circle me-2 text-primary"></i>
               <strong>{intl.formatMessage(M.approvalLabel)}</strong> {intl.formatMessage(M.approvalHint)}
            </div>

          </div>
          <div className="col-md-5">
             <h6 className="fw-bolder mb-3 text-muted border-bottom pb-2">
               {intl.formatMessage(M.previewTitle)}
             </h6>
             <div className="d-flex justify-content-center">
               <WhatsAppPreview 
                  message={form.body_text} 
                  header={undefined} 
                  footer={form.footer_text} 
               />
             </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          {intl.formatMessage(M.cancel)}
        </Button>
        <Button variant="success" onClick={handleCreate} disabled={loading || !form.friendly_name || !form.body_text}>
          {loading ? <Spinner size="sm" animation="border" /> : intl.formatMessage(M.submit)}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
